import { useCallback, useEffect, useState, type KeyboardEvent } from 'react';
import { api } from '../api/client';
import { useT } from '../i18n/i18n';
import { useLearner } from '../learners/LearnerContext';
import type { ProgramView } from '../programs/types';
import { fetchGoals, fetchSessions, moveSession, type Goal, type Session } from './api';
import { DayView, ListView, MonthView, WeekView, shiftAnchor, viewRange, type CalView } from './Calendar';
import { addDays, today, useDates } from './dates';
import ExportDialog from './ExportDialog';
import GoalsBoard from './GoalsBoard';
import ProgressView from './ProgressView';
import TimelineView from './TimelineView';
import ReviewsView from './ReviewsView';
import SessionEditor from './SessionEditor';
import SessionView from './SessionView';
import './calendar.css';

type Tab = 'calendar' | 'goals' | 'progress' | 'timeline' | 'reviews';
const TABS: Tab[] = ['calendar', 'goals', 'progress', 'timeline', 'reviews'];
const TAB_LABEL: Record<Tab, string> = { calendar: 'Calendar', goals: 'Goals and targets', progress: 'Progress', timeline: 'Timeline', reviews: 'Reviews' };

const TAB_KEY = 'easylearning.planner.tab';
const VIEW_KEY = 'easylearning.planner.view';
const NARROW = '(max-width: 720px)';

function readStored<T extends string>(key: string, allowed: T[], fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    return allowed.includes(v as T) ? (v as T) : fallback;
  } catch {
    return fallback;
  }
}

function store(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    return;
  }
}

function useNarrow(): boolean {
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia(NARROW).matches);
  useEffect(() => {
    const mq = window.matchMedia(NARROW);
    const on = () => setNarrow(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return narrow;
}

export default function PlannerPage() {
  const t = useT();
  const d = useDates();
  const { learnerId, learner } = useLearner();
  const narrow = useNarrow();
  const [tab, setTab] = useState<Tab>(() => readStored<Tab>(TAB_KEY, TABS, 'calendar'));
  const [chosenView, setChosenView] = useState<CalView | null>(() => {
    try {
      const v = localStorage.getItem(VIEW_KEY);
      return v === 'month' || v === 'week' || v === 'day' || v === 'list' ? v : null;
    } catch {
      return null;
    }
  });
  const [anchor, setAnchor] = useState(today());
  const [sessions, setSessions] = useState<Session[]>([]);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [programs, setPrograms] = useState<ProgramView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ session: Session | null; day: string } | null>(null);
  const [openSession, setOpenSession] = useState<Session | null>(null);
  const [exporting, setExporting] = useState(false);

  const view: CalView = narrow
    ? (chosenView === 'day' ? 'day' : 'list')
    : (chosenView ?? 'month');
  const views: CalView[] = narrow ? ['day', 'list'] : ['month', 'week', 'day', 'list'];
  const { from, to } = viewRange(view, anchor);

  const loadSessions = useCallback(async () => {
    if (!learnerId) return;
    setLoading(true);
    try {
      const list = await fetchSessions(learnerId, from, to);
      setSessions(list);
      setOpenSession((o) => (o ? list.find((s) => s.id === o.id) ?? o : o));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('The calendar could not be loaded just now.'));
    } finally {
      setLoading(false);
    }
  }, [learnerId, from, to, t]);

  useEffect(() => { void loadSessions(); }, [loadSessions]);

  const loadPlanning = useCallback(async () => {
    if (!learnerId) return;
    try {
      const [g, p] = await Promise.all([
        fetchGoals(learnerId),
        api<ProgramView[]>(`/programs?learnerId=${encodeURIComponent(learnerId)}`, { token: true }),
      ]);
      setGoals(g);
      setPrograms(p);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('Goals and programs could not be loaded just now.'));
    }
  }, [learnerId, t]);

  useEffect(() => { if (tab === 'calendar') void loadPlanning(); }, [loadPlanning, tab]);

  function chooseTab(next: Tab) {
    setTab(next);
    store(TAB_KEY, next);
  }

  function chooseView(next: CalView) {
    setChosenView(next);
    store(VIEW_KEY, next);
  }

  function onTabKey(e: KeyboardEvent<HTMLDivElement>) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
    e.preventDefault();
    const at = TABS.indexOf(tab);
    const step = (e.key === 'ArrowLeft') !== (document.documentElement.dir === 'rtl') ? -1 : 1;
    const next: Tab = e.key === 'Home' ? TABS[0] : e.key === 'End' ? TABS[TABS.length - 1] : TABS[(at + step + TABS.length) % TABS.length];
    chooseTab(next);
    document.getElementById(`planner-tab-${next}`)?.focus();
  }

  async function move(s: Session, day: string) {
    if (!learnerId) return;
    try {
      await moveSession(learnerId, s.id, day);
      setOpenSession((o) => (o && o.id === s.id ? { ...o, day, seriesId: null, repeat: null } : o));
      await loadSessions();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('The session could not be moved just now.'));
    }
  }

  const openDay = (day: string) => { setAnchor(day); chooseView('day'); };
  const addOn = (day: string) => setEditing({ session: null, day });

  const title = view === 'day'
    ? d.long(anchor)
    : view === 'week'
      ? t('{from} to {to}', { from: d.short(from), to: d.short(addDays(from, 6)) })
      : d.month(anchor);

  const common = {
    anchor,
    sessions,
    onOpen: (s: Session) => setOpenSession(s),
    onEdit: (s: Session) => setEditing({ session: s, day: s.day }),
    onMove: move,
    onAdd: addOn,
    onDay: openDay,
  };

  return (
    <section aria-labelledby="planner-h" className="planner">
      <div className="planner-cal-top">
        <h2 id="planner-h">{t('Plan')}</h2>
        {tab === 'calendar' && learnerId && (
          <div className="toolbar">
            <button type="button" className="btn" onClick={() => addOn(anchor)}>{t('Add a session')}</button>
            <button type="button" className="btn secondary" onClick={() => setExporting(true)}>{t('Export')}</button>
          </div>
        )}
      </div>

      <div className="tabs" role="tablist" aria-label={t('Plan')} onKeyDown={onTabKey}>
        {TABS.map((id) => (
          <button
            key={id}
            id={`planner-tab-${id}`}
            type="button"
            role="tab"
            className={`tab${tab === id ? ' is-active' : ''}`}
            aria-selected={tab === id}
            aria-controls={`planner-panel-${id}`}
            tabIndex={tab === id ? 0 : -1}
            onClick={() => chooseTab(id)}
          >
            {t(TAB_LABEL[id])}
          </button>
        ))}
      </div>

      {!learnerId ? (
        <p className="muted">{t('Choose a learner to see their plan.')}</p>
      ) : tab === 'goals' ? (
        <div role="tabpanel" id="planner-panel-goals" aria-labelledby="planner-tab-goals">
          <GoalsBoard />
        </div>
      ) : tab === 'progress' ? (
        <div role="tabpanel" id="planner-panel-progress" aria-labelledby="planner-tab-progress">
          <ProgressView />
        </div>
      ) : tab === 'timeline' ? (
        <div role="tabpanel" id="planner-panel-timeline" aria-labelledby="planner-tab-timeline">
          <TimelineView />
        </div>
      ) : tab === 'reviews' ? (
        <div role="tabpanel" id="planner-panel-reviews" aria-labelledby="planner-tab-reviews">
          <ReviewsView />
        </div>
      ) : (
        <div role="tabpanel" id="planner-panel-calendar" aria-labelledby="planner-tab-calendar">
          <div className="planner-cal-bar">
            <h3 id="planner-cal-title" className="planner-cal-title" aria-live="polite">{title}</h3>
            <div className="toolbar">
              <button type="button" className="btn secondary" onClick={() => setAnchor(shiftAnchor(view, anchor, -1))}>{t('← Previous')}</button>
              <button type="button" className="btn secondary" onClick={() => setAnchor(today())}>{t('Today')}</button>
              <button type="button" className="btn secondary" onClick={() => setAnchor(shiftAnchor(view, anchor, 1))}>{t('Next →')}</button>
            </div>
            <div className="toolbar planner-cal-views" role="group" aria-label={t('View')}>
              {views.map((v) => (
                <button key={v} type="button" className={view === v ? 'btn' : 'btn secondary'} aria-pressed={view === v} onClick={() => chooseView(v)}>
                  {v === 'month' ? t('Month') : v === 'week' ? t('Week') : v === 'day' ? t('Day') : t('List')}
                </button>
              ))}
            </div>
          </div>

          {error && <div className="error" role="alert">{error}</div>}
          {loading && sessions.length === 0 && <p className="muted">{t('Loading…')}</p>}

          {view === 'month' && <MonthView {...common} setAnchor={setAnchor} labelledBy="planner-cal-title" />}
          {view === 'week' && <WeekView {...common} />}
          {view === 'day' && <DayView {...common} />}
          {view === 'list' && <ListView {...common} />}

          {(view === 'month' || view === 'week') && (
            <p className="field-hint planner-cal-help">
              {t('To move a session, drag it to another day, or open its summary and choose Move to….')}
            </p>
          )}
        </div>
      )}

      {editing && learnerId && (
        <SessionEditor
          learnerId={learnerId}
          session={editing.session}
          day={editing.day}
          goals={goals}
          programs={programs}
          onClose={() => setEditing(null)}
          onSaved={() => { setOpenSession(null); void loadSessions(); }}
        />
      )}
      {openSession && learnerId && !editing && (
        <SessionView
          key={openSession.id}
          learnerId={learnerId}
          session={openSession}
          goals={goals}
          onClose={() => setOpenSession(null)}
          onChanged={loadSessions}
          onEdit={(s) => setEditing({ session: s, day: s.day })}
          onMove={move}
        />
      )}
      {exporting && learnerId && <ExportDialog learnerId={learnerId} learnerName={learner?.name ?? null} onClose={() => setExporting(false)} />}
    </section>
  );
}
